import { CommonModule } from "@angular/common";
import { Component, effect } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import {
  NgbDatepickerConfig,
  NgbDatepickerModule,
  NgbDateStruct,
  NgbDatepickerI18n,
} from "@ng-bootstrap/ng-bootstrap";
import {
  AvailableTimesModel,
  GroupedTimesModel,
} from "../../models/appointment/appoinment.model";
import { NgSelectModule } from "@ng-select/ng-select";
import { environment } from "../../../environments/environment";
import { DepartmentLocalizationService } from "../../shared/services/department-localization.service";
import { HeaderService } from "../../shared/services/header.service";
import { ToastService } from "../../shared/services/toast.service";
import { LocalizedDatepickerI18n } from "../../shared/i18n/datepicker-i18n.service";
import { LocaleService } from "../../shared/i18n/locale.service";
import { TranslatePipe } from "../../shared/i18n/translate.pipe";

type DepartmentOption = {
  id: string;
  name: string;
  rawName?: string;
  icon?: string;
};

@Component({
  selector: "app-create-appoinment",
  standalone: true,
  imports: [
    CommonModule,
    NgbDatepickerModule,
    FormsModule,
    NgSelectModule,
    RouterModule,
    TranslatePipe,
  ],
  providers: [
    { provide: NgbDatepickerI18n, useClass: LocalizedDatepickerI18n },
  ],
  templateUrl: "./create-appoinment.component.html",
  styleUrl: "./create-appoinment.component.scss",
})
export class CreateAppoinmentComponent {
  private readonly preparationStorageKey = "preparationFormData";
  model!: NgbDateStruct;
  today: NgbDateStruct;
  useMockTimes: boolean = true;
  appoinmentTypes: { id: string; name: string }[] = [];
  resources: { id: string; name: string; languageCode: string }[] = [];
  availableTimes: AvailableTimesModel[] = [];
  selectedAppoinmentType: string | null = null;
  selectedResource: string | null = "";
  organizations: { id: string; name: string }[] = [];
  facilities: { id: string; name: string }[] = [];
  departments: DepartmentOption[] = [];
  doctors: { id: string; name: string; professionalTitle?: number | null }[] =
    [];
  selectedOrganization: string | null = null;
  selectedFacility: string | null = null;
  selectedDepartment: string | null = null;
  selectedDoctor: string | null = null;
  selectedDate: string | null = null;
  selectedTime: string | null = null;
  timeListLoading: boolean = true;
  groupedTimes: GroupedTimesModel[] = [];
  customerTimeDifference: string | null = "+0";
  applicationType: string = "Custom";
  submitLoading: boolean = false;
  busySlots: {
    startAt?: string;
    endAt?: string;
    StartAt?: string;
    EndAt?: string;
    isBlocked?: boolean;
    isReserved?: boolean;
    IsBlocked?: boolean;
    IsReserved?: boolean;
  }[] = [];
  busySlotsReady: boolean = false;
  private readonly appointmentApiBase = environment.apiLocations.appointment;
  private timeChipStates = new Map<string, boolean>();
  constructor(
    private router: Router,
    config: NgbDatepickerConfig,
    private activatedRoute: ActivatedRoute,
    private http: HttpClient,
    private departmentLocalizationService: DepartmentLocalizationService,
    private headerService: HeaderService,
    private toastService: ToastService,
    private localeService: LocaleService,
  ) {
    const currentDate = new Date();
    this.today = {
      year: currentDate.getFullYear(),
      month: currentDate.getMonth() + 1,
      day: currentDate.getDate(),
    };
    this.selectedDate = `${String(this.today.day).padStart(2, "0")}-${String(
      this.today.month,
    ).padStart(2, "0")}-${this.today.year}`;

    config.firstDayOfWeek = 1;
    this.activatedRoute.queryParams.subscribe((params) => {
      this.applicationType = params["applicationType"] || "Custom";
    });

    effect(() => {
      this.localeService.locale();
      void this.refreshLocalizedDepartments();
    });
  }

  private readonly identityApiBase = environment.apiLocations.identity;

  ngOnInit(): void {
    const prepData = localStorage.getItem(this.preparationStorageKey);
    if (!prepData) {
      this.router.navigate(["/preparation"], {
        queryParamsHandling: "preserve",
      });
      return;
    }
    if (this.useMockTimes) {
      this.setMockTimes();
    }
    this.updateCurrentTimeSeconds();
    this.loadOrganizations();
  }

  isDisabled(date: { year: number; month: number; day: number }): boolean {
    const today = new Date();
    const nextYear = new Date(today);
    nextYear.setFullYear(today.getFullYear() + 1);

    const minDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );
    const maxDate = new Date(
      nextYear.getFullYear(),
      nextYear.getMonth(),
      nextYear.getDate(),
    );

    const currentDate = new Date(date.year, date.month - 1, date.day);

    return !(currentDate >= minDate && currentDate <= maxDate);
  }

  dateSelect(date: { year: number; month: number; day: number }) {
    this.timeChipStates.clear();

    const formattedDate = `${date.year}-${String(date.month).padStart(
      2,
      "0",
    )}-${String(date.day).padStart(2, "0")}`;
    this.selectedDate = formattedDate;
    this.updateCurrentTimeSeconds();
    this.busySlotsReady = false;
    if (this.selectedDoctor) {
      this.loadBusySlots();
    }
    this.getAppointmentTimeListCommon(
      formattedDate,
      this.selectedResource ?? "",
      this.selectedAppoinmentType ?? "",
    );
  }

  getAppointmentTimeListCommon(
    date: string,
    resource: string,
    appointmentType: string,
  ) {
    if (this.useMockTimes) {
      this.setMockTimes();
      return;
    }
    this.timeListLoading = true;
    if (resource) {
    }
  }

  getAppointmentTimeListWithSelectedResource() {
    const formattedDate = `${this.today.year}-${String(
      this.today.month,
    ).padStart(2, "0")}-${String(this.today.day).padStart(2, "0")}`;
    this.getAppointmentTimeListCommon(
      formattedDate,
      this.selectedResource ?? "",
      this.selectedAppoinmentType ?? "",
    );
  }

  getAppointmentTimeListWithSelectedAppointment() {
    const formattedDate = `${this.today.year}-${String(
      this.today.month,
    ).padStart(2, "0")}-${String(this.today.day).padStart(2, "0")}`;
    this.getAppointmentTimeListCommon(
      formattedDate,
      this.selectedResource ?? "",
      this.selectedAppoinmentType ?? "",
    );
  }

  navigateToPreparation(time: string): void {
    this.selectedTime = time;
    if (
      !this.selectedOrganization ||
      !this.selectedFacility ||
      !this.selectedDepartment ||
      !this.selectedDoctor
    ) {
      this.displayToast(
        true,
        this.localeService.t("create.toasts.missingCoreSelection"),
        "warning",
      );
      return;
    }

    const selectedDepartment = this.departments.find(
      (item) => item.id === this.selectedDepartment,
    );
    const selectedDoctor = this.doctors.find(
      (item) => item.id === this.selectedDoctor,
    );
    const selectedFacility = this.facilities.find(
      (item) => item.id === this.selectedFacility,
    );
    const selectedOrganization = this.organizations.find(
      (item) => item.id === this.selectedOrganization,
    );

    this.router.navigate(["/preparation"], {
      queryParamsHandling: "preserve",
      state: {
        appoinmentType: selectedDoctor ?? selectedDepartment,
        resource: {
          id: selectedFacility?.id || "",
          name: selectedFacility?.name || "",
          customerId: selectedOrganization?.id || "",
          languageCode: this.localeService.getApiLanguage(),
        },
        selectedDate: this.selectedDate,
        selectedTime: time,
        timeDifference: this.customerTimeDifference,
        applicationType: this.applicationType,
      },
    });
  }

  selectTime(time: string): void {
    if (!time || this.isDisabledTimeButton(time)) return;
    this.selectedTime = time;
  }

  displayToast(show: boolean, message: string, type: string | any) {
    if (!show) {
      this.toastService.hideToast();
      return;
    }
    this.toastService.showToast(message, type);
  }

  groupByHourRange(): GroupedTimesModel[] {
    const groupedTimes: GroupedTimesModel[] = [];
    for (let i = 0; i < this.availableTimes.length; i += 2) {
      const startHour = this.availableTimes[i].hour;
      const endHour = this.availableTimes[i + 1]
        ? this.availableTimes[i + 1].hour
        : "";

      const range = `${startHour}-${endHour}`;

      groupedTimes.push({
        range,
        availableTimes: this.availableTimes.slice(i, i + 2),
      });
    }

    return groupedTimes;
  }

  private getTimeSlot(hour: string): string {
    const start = hour.split(":")[0]; // Get the hour part
    const end = (parseInt(start) + 1) % 24; // Get the next hour for the range
    return `${start}:00-${end.toString().padStart(2, "0")}:00`; // Format as HH:00-HH:00
  }

  private groupByTimeSlot(): any[] {
    const result = [];
    let currentSlot: string = "";
    let currentSlotData: AvailableTimesModel[] = [];

    const filteredTimes = this.availableTimes.filter((item) =>
      this.isWithinWorkingHours(item.hour),
    );

    filteredTimes.forEach((item) => {
      const slot = this.getTimeSlot(item.hour);
      if (slot !== currentSlot) {
        if (currentSlot) {
          result.push({ range: currentSlot, availableTimes: currentSlotData });
        }
        currentSlot = slot;
        currentSlotData = [item];
      } else {
        currentSlotData.push(item);
      }
    });

    if (currentSlot) {
      result.push({ range: currentSlot, availableTimes: currentSlotData });
    }

    return result;
  }

  private isWithinWorkingHours(hour: string): boolean {
    const h = Number(hour.split(":")[0]);
    return h >= 8 && h <= 17;
  }

  private setMockTimes(): void {
    this.timeListLoading = false;
    const mockTimes: AvailableTimesModel[] = [];
    for (let hour = 8; hour <= 17; hour++) {
      const h = String(hour).padStart(2, "0");
      mockTimes.push({ hour: `${h}:00`, isAvailable: true });
      mockTimes.push({ hour: `${h}:15`, isAvailable: true });
      mockTimes.push({ hour: `${h}:30`, isAvailable: true });
      mockTimes.push({ hour: `${h}:45`, isAvailable: true });
    }
    this.availableTimes = mockTimes;
    this.groupedTimes = this.groupByTimeSlot();
  }

  createAppointment(): void {
    const prepDataRaw = localStorage.getItem(this.preparationStorageKey);
    if (!prepDataRaw) {
      this.router.navigate(["/preparation"], {
        queryParamsHandling: "preserve",
      });
      return;
    }

    if (!this.selectedDate || !this.selectedTime || !this.selectedDoctor) {
      this.displayToast(
        true,
        this.localeService.t("create.toasts.missingAppointmentSelection"),
        "warning",
      );
      return;
    }

    if (this.isDisabledTimeButton(this.selectedTime)) {
      this.displayToast(
        true,
        this.localeService.t("create.toasts.unavailableTime"),
        "warning",
      );
      this.selectedTime = null;
      return;
    }

    const prepData = JSON.parse(prepDataRaw);
    const selectedDoctor = this.doctors.find(
      (item) => item.id === this.selectedDoctor,
    );
    const selectedOrganization = this.organizations.find(
      (item) => item.id === this.selectedOrganization,
    );
    const selectedDepartment = this.departments.find(
      (item) => item.id === this.selectedDepartment,
    );

    const normalizedDate = this.normalizeDateForApi(this.selectedDate);
    if (!normalizedDate) {
      this.displayToast(
        true,
        this.localeService.t("create.toasts.invalidDate"),
        "warning",
      );
      return;
    }

    const startDate = new Date(`${normalizedDate}T${this.selectedTime}:00`);
    if (Number.isNaN(startDate.getTime())) {
      this.displayToast(
        true,
        this.localeService.t("create.toasts.invalidTime"),
        "warning",
      );
      return;
    }
    const startAt = this.formatLocalIso(startDate);
    const endDate = new Date(startDate.getTime());
    if (Number.isNaN(endDate.getTime())) {
      this.displayToast(
        true,
        this.localeService.t("create.toasts.invalidTime"),
        "warning",
      );
      return;
    }
    endDate.setMinutes(endDate.getMinutes() + 15);
    const endAt = this.formatLocalIso(endDate);

    const payload = {
      ...prepData,
      PatientId: prepData?.PatientId ?? null,
      CountryId: "225",
      CityId: prepData?.City ?? null,
      DistrictId: prepData?.District ?? null,
      OrganizationId: this.selectedOrganization || "",
      OrganizationName: selectedOrganization?.name || "",
      DepartmentId: this.selectedDepartment || null,
      DepartmentName:
        selectedDepartment?.rawName ?? selectedDepartment?.name ?? null,
      DoctorId: this.selectedDoctor,
      DoctorName: selectedDoctor?.name || "",
      ProfessionalTitle: selectedDoctor?.professionalTitle ?? null,
      StartAt: startAt,
      EndAt: endAt,
    };

    this.submitLoading = true;
    this.http
      .post<any>(`${this.appointmentApiBase}/Appointments`, payload, {
        ...this.getHttpOptions(),
      })
      .subscribe({
        next: (res) => {
          this.submitLoading = false;
          const appointmentId = res?.data?.appointmentId;
          const patientId = res?.data?.patientId;
          if (appointmentId && patientId) {
            this.displayToast(
              true,
              this.localeService.t("create.toasts.created"),
              "success",
            );
          } else {
            this.displayToast(
              true,
              this.localeService.t("create.toasts.createdFallback"),
              "success",
            );
          }
          localStorage.removeItem(this.preparationStorageKey);
          setTimeout(() => {
            this.router.navigate(["/preparation"], {
              queryParamsHandling: "preserve",
            });
          }, 800);
        },
        error: () => {
          this.submitLoading = false;
          this.displayToast(
            true,
            this.localeService.t("create.toasts.createError"),
            "error",
          );
        },
      });
  }

  private normalizeDateForApi(dateStr: string): string | null {
    if (!dateStr) return null;
    // Accept YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    // Accept DD-MM-YYYY
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
      const [day, month, year] = dateStr.split("-").map(Number);
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(
        2,
        "0",
      )}`;
    }
    return null;
  }

  private formatLocalIso(date: Date): string {
    const pad = (value: number) => String(value).padStart(2, "0");
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  }

  private readonly orgApiBase = environment.apiLocations.organization;
  private readonly orgDeptApiBase = environment.apiLocations.organizationDept;

  private getHttpOptions() {
    return { headers: this.headerService.getHeadersWithoutAuth() };
  }

  private normalizeList<T>(response: any): T[] {
    if (Array.isArray(response)) return response as T[];
    if (Array.isArray(response?.data)) return response.data as T[];
    if (Array.isArray(response?.Data)) return response.Data as T[];
    return [];
  }

  loadOrganizations(): void {
    this.http
      .get<any>(`${this.orgApiBase}/Organizations/all`, this.getHttpOptions())
      .subscribe({
        next: (res) => {
          const list = this.normalizeList<{ id: string; name: string }>(res);
          this.organizations = list;
        },
        error: () => {
          this.organizations = [];
        },
      });
  }

  onOrganizationChange(orgId: string | null): void {
    const normalizedOrgId = typeof orgId === "string" ? orgId : "";
    this.selectedOrganization = normalizedOrgId;
    this.selectedFacility = null;
    this.selectedDepartment = null;
    this.selectedDoctor = null;
    this.facilities = [];
    this.departments = [];
    this.doctors = [];
    if (!normalizedOrgId) return;

    this.http
      .get<any>(
        `${this.orgApiBase}/facilities/by-organization/${normalizedOrgId}`,
        this.getHttpOptions(),
      )
      .subscribe({
        next: (res) => {
          const list = this.normalizeList<{ id: string; name: string }>(res);
          this.facilities = list;
        },
        error: () => {
          this.facilities = [];
        },
      });
  }

  async onFacilityChange(facilityId: string | null): Promise<void> {
    const normalizedFacilityId =
      typeof facilityId === "string" ? facilityId : "";
    this.selectedFacility = normalizedFacilityId;
    this.selectedDepartment = null;
    this.selectedDoctor = null;
    this.departments = [];
    this.doctors = [];
    if (!normalizedFacilityId) return;

    this.http
      .get<any>(
        `${this.orgDeptApiBase}/departments/by-facility/${normalizedFacilityId}`,
        this.getHttpOptions(),
      )
      .subscribe({
        next: async (res) => {
          const list = this.normalizeList<{ id: string; name: string }>(res);
          this.departments = await this.localizeDepartments(list);
        },
        error: () => {
          this.departments = [];
        },
      });
  }

  private async localizeDepartments(
    list: { id: string; name: string }[],
  ): Promise<DepartmentOption[]> {
    const allDepartments = await this.departmentLocalizationService.getAll();
    return list.map((dep) => {
      const match = allDepartments.find(
        (item) => item.value === dep.name || item.label === dep.name,
      );

      if (match) {
        return {
          ...dep,
          rawName: dep.name,
          icon: match.icon,
          name: match.label,
        };
      }

      return {
        ...dep,
        rawName: dep.name,
      };
    });
  }

  private async refreshLocalizedDepartments(): Promise<void> {
    if (!this.departments.length) {
      return;
    }

    this.departments = await this.localizeDepartments(
      this.departments.map((department) => ({
        id: department.id,
        name: department.rawName ?? department.name,
      })),
    );
  }

  onDepartmentChange(departmentId: string | null): void {
    const normalizedDepartmentId =
      typeof departmentId === "string" ? departmentId : "";
    this.selectedDepartment = normalizedDepartmentId;
    this.selectedDoctor = null;
    this.doctors = [];
    if (
      !normalizedDepartmentId ||
      !this.selectedOrganization ||
      !this.selectedFacility
    ) {
      return;
    }

    this.http
      .get<any>(`${this.identityApiBase}/users/get-specialist-users`, {
        ...this.getHttpOptions(),
        params: {
          organizationId: this.selectedOrganization,
          facilityId: this.selectedFacility,
          departmentId: normalizedDepartmentId,
        },
      })
      .subscribe({
        next: (res) => {
          const list = this.normalizeList<{
            id: string;
            name: string;
            professionalTitle?: number | null;
          }>(res);
          this.doctors = list;
        },
        error: () => {
          this.doctors = [];
        },
      });
  }

  onDoctorChange(doctorId: string | null): void {
    this.selectedDoctor = typeof doctorId === "string" ? doctorId : null;
    this.busySlots = [];
    this.busySlotsReady = false;
    this.timeChipStates.clear();
    this.selectedTime = null;
    if (this.selectedDoctor && this.selectedDate) {
      this.loadBusySlots();
    }
  }

  private loadBusySlots(): void {
    if (!this.selectedDoctor || !this.selectedDate) return;
    const normalizedDate = this.normalizeDateForApi(this.selectedDate);
    if (!normalizedDate) return;

    this.busySlotsReady = false;
    this.http
      .get<any>(
        `${this.appointmentApiBase}/Appointments/doctors/${this.selectedDoctor}/busy-slots`,
        {
          ...this.getHttpOptions(),
          params: { date: normalizedDate },
        },
      )
      .subscribe({
        next: (res) => {
          const list = this.normalizeList<any>(res);
          this.busySlots = list;
          this.busySlotsReady = true;
          this.timeChipStates.clear();
          this.clearSelectedTimeIfDisabled();
        },
        error: () => {
          this.busySlots = [];
          this.busySlotsReady = true;
          this.timeChipStates.clear();
          this.clearSelectedTimeIfDisabled();
        },
      });
  }

  hasAvailableTime(times: AvailableTimesModel[] | undefined) {
    if (this.isCalendarDateBiggerThenToday()) return true;
    if (!times?.length) return false;
    let result = false;
    for (const item of [...times].reverse()) {
      if (item.isAvailable && !this.isDisabledTimeButton(item.hour)) {
        result = true;
        break;
      }
    }
    return result;
  }

  isDisabledTimeButton(time: string | undefined) {
    try {
      if (!this.selectedDoctor || !this.selectedDate) return true;
      if (!this.busySlotsReady) return true;
      if (this.isTimeBusy(time)) return true;
      if (this.useMockTimes) return false;
      if (this.isCalendarDateBiggerThenToday()) return false;
      if (!time) return true;
      let buttonTime = time;
      if (buttonTime.length === 5) buttonTime += ":00";
      const buttonTimeParts = buttonTime.split(":").map(Number);
      const buttonTimeSeconds =
        buttonTimeParts[0] * 3600 +
        buttonTimeParts[1] * 60 +
        buttonTimeParts[2];
      return buttonTimeSeconds < this.currentTimeSeconds;
    } catch (e) {
      return true;
    }
  }

  private isTimeBusy(time: string | undefined): boolean {
    if (!time || !this.selectedDate) return false;
    if (!this.busySlots.length) return false;
    const normalizedDate = this.normalizeDateForApi(this.selectedDate);
    if (!normalizedDate) return false;
    const [year, month, day] = normalizedDate.split("-").map(Number);
    const [hour, minute] = time.split(":").map(Number);
    const slotUtcMs = Date.UTC(year, month - 1, day, hour, minute, 0);
    if (Number.isNaN(slotUtcMs)) return false;

    return this.busySlots.some((slot) => {
      const isBlocked = !!(slot.isBlocked ?? slot.IsBlocked);
      const isReserved = !!(slot.isReserved ?? slot.IsReserved);
      if (!isBlocked && !isReserved) return false;
      const startRaw = slot.startAt ?? slot.StartAt;
      const endRaw = slot.endAt ?? slot.EndAt;
      if (!startRaw || !endRaw) return false;
      const startMs = new Date(startRaw).getTime();
      const endMs = new Date(endRaw).getTime();
      if (Number.isNaN(startMs) || Number.isNaN(endMs)) return false;
      return slotUtcMs >= startMs && slotUtcMs < endMs;
    });
  }

  private currentTimeSeconds = 0;

  private updateCurrentTimeSeconds(): void {
    const now = new Date();
    this.currentTimeSeconds =
      now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
  }

  private clearSelectedTimeIfDisabled(): void {
    if (!this.selectedTime) return;
    if (!this.selectedDoctor || !this.selectedDate) return;
    if (!this.busySlotsReady) return;
    if (this.isDisabledTimeButton(this.selectedTime)) {
      this.selectedTime = null;
    }
  }

  trackByRange = (index: number, item: GroupedTimesModel): string => {
    return `${item.range}-${index}`;
  };

  trackByHour = (index: number, item: AvailableTimesModel): string => {
    return `${item.hour}-${item.isAvailable}`;
  };

  isTimeChipDisabled(time: AvailableTimesModel): boolean {
    const key = `${time.hour}-${this.selectedDate}`;

    if (!this.timeChipStates.has(key)) {
      const disabled =
        !time.isAvailable || this.isDisabledTimeButton(time.hour);
      this.timeChipStates.set(key, disabled);
    }

    return this.timeChipStates.get(key)!;
  }

  isCalendarDateBiggerThenToday() {
    if (this.useMockTimes) return true;
    const calendarDate = new Date(
      this.today.year,
      this.today.month - 1,
      this.today.day,
    );
    const currentDate = new Date();
    if (calendarDate > currentDate) return true;
    return false;
  }

  get selectedDateDisplayText(): string {
    if (!this.selectedDate) {
      return "";
    }

    const normalizedDate = this.normalizeDateForApi(this.selectedDate);
    if (!normalizedDate) {
      return this.selectedDate;
    }

    return this.localeService.formatDate(normalizedDate, {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  }
}
